import { Icon, Tag, Text, colors } from "@asurion-core/mx-asurion-ui-react";
import {
  DetailsContainer,
  DetailsWrapperContainer,
  HighlightText,
  WorkOrderPrograms,
  WorkOrderStatus,
  workorderProgrammNameWithTags,
  workorderStatusWithTag
} from "@ubif/components";
import { formatDateAsMMDDYYY } from "@ubif/util-store";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { FlexContactWrapperContainer, FlexNameWrapperContainer } from "./FlexContainers";
import { QuickSearchCustomerDetailsProps } from "./QuickSearchCustomerDetails";

type QuickSearchWorkorderDetailsProps = Omit<
  QuickSearchCustomerDetailsProps,
  "firstName" | "lastName" | "customerId" | "onClick"
> & {
  workorderId: number;
  workorderStatus: {
    workorderStatusId: number;
    name: string;
  };
  program?: {
    id: number;
    name: string;
  };
  claimNumber: string;
  updatedAt: Date | string;
  deviceName: string;
  isRefurbishedDeviceSale: boolean;
  onClick: (id: number) => void;
};

export const QuickSearchWorkorderDetails = ({
  fullName,
  workorderId,
  claimNumber,
  workorderStatus,
  program,
  highlightText,
  onClick,
  updatedAt,
  deviceName,
  isRefurbishedDeviceSale
}: QuickSearchWorkorderDetailsProps) => {
  const { t } = useTranslation("root-config");

  const handleClick = () => {
    onClick(workorderId);
  };

  return (
    <StyledQuickSearchWorkorderDetails onClick={handleClick}>
      <Icon aria-hidden="true" fill={colors.neutralDeepest} size="medium" src="wrench" />
      <DetailsContainer>
        <FlexNameWrapperContainer>
          <HighlightText
            highlightText={highlightText}
            wholeText={`${t("GlobalSearch.WorkordersWithId", { workorderId })} |`}
          />
          <HighlightText highlightText={highlightText} wholeText={fullName} className="fs-mask" />
        </FlexNameWrapperContainer>
        <FlexNameWrapperContainer>
          <StyledContact>
            <Icon src="time/calendar-check-2.svg" size="normal" />
            <StyledContactText>
              <Text size={1} color={colors.neutralDeepest}>
                {formatDateAsMMDDYYY(typeof updatedAt === "string" ? updatedAt : updatedAt.toISOString())}
              </Text>
            </StyledContactText>
          </StyledContact>
          <StyledContact>
            <StyledImage src="/static/deviceimages/ICR_smartphone.svg" />
            <StyledContactText>
              <Text size={1} color={colors.neutralDeepest}>
                {deviceName}
              </Text>
            </StyledContactText>
          </StyledContact>
        </FlexNameWrapperContainer>
        <FlexContactWrapperContainer>
          <StyledContact>
            <Tag text={program.name} type={workorderProgrammNameWithTags[program.name as WorkOrderPrograms]} />
            <Tag text={workorderStatus.name} type={workorderStatusWithTag[workorderStatus.name as WorkOrderStatus]} />
            {isRefurbishedDeviceSale && <Tag text={t("RefurbishedDeviceSale")} type={"update"} />}
          </StyledContact>
        </FlexContactWrapperContainer>
        <FlexContactWrapperContainer>
          {claimNumber ? <Tag text={t("GlobalSearch.ClaimNo", { claimNumber })} type="gray" /> : null}
        </FlexContactWrapperContainer>
      </DetailsContainer>
    </StyledQuickSearchWorkorderDetails>
  );
};

const StyledQuickSearchWorkorderDetails = styled(DetailsWrapperContainer)`
  background-color: var(--aui-elevated-level-01);
  color: var(--aui-on-surface);

  &:hover {
    background-color: var(--aui-outline-soft);
    border-radius: 0.5rem;
    cursor: pointer;
  }
`;
const StyledContact = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

const StyledContactText = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
`;

const StyledImage = styled.img`
  width: 1rem;
  height: auto;
`;
